import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentMethodCreateParams, type PaymentMethod } from '@stripe/stripe-js';
import { useCallback } from 'react';

export type GetClientSecretResult =
	| {
			clientSecret: string;
			error?: undefined;
	  }
	| {
			clientSecret?: undefined;
			error: Error;
	  };

export type UseStripePaymentProps = {
	getClientSecret: (paymentMethod: PaymentMethod) => Promise<GetClientSecretResult>;
};

export default function useSubmitStripePayment({ getClientSecret }: UseStripePaymentProps) {
	const elements = useElements();
	const stripe = useStripe();

	const submitStripePayment = useCallback(
		async (billingDetails: PaymentMethodCreateParams.BillingDetails = {}) => {
			if (!elements || !stripe) {
				return { error: new Error('Stripe has not loaded yet. Please try again in a moment.') };
			}

			const { error: submitError } = await elements.submit();
			if (submitError) {
				return { error: submitError };
			}

			const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
				elements,
				params: {
					billing_details: billingDetails,
				},
			});
			if (paymentMethodError) {
				return { error: paymentMethodError };
			}

			const { clientSecret, error: clientSecretError } = await getClientSecret(paymentMethod);
			if (clientSecretError) {
				return { error: clientSecretError };
			}

			const isSetupIntent = clientSecret.startsWith('seti_');

			const confirmMethod = isSetupIntent ? 'confirmSetup' : 'confirmPayment';

			const confirmResult = await stripe[confirmMethod]({
				clientSecret,
				confirmParams: {
					payment_method: paymentMethod.id,
					return_url: window.location.href,
				},
				redirect: 'if_required',
			});
			if (confirmResult.error) {
				return {
					error: confirmResult.error,
				};
			}

			const status =
				'setupIntent' in confirmResult
					? confirmResult.setupIntent.status
					: confirmResult.paymentIntent.status;

			return { isSetupIntent, status };
		},
		[elements, getClientSecret, stripe],
	);

	return submitStripePayment;
}
